// Defined based on http://shub/color/
export const defaultAccentColorRamp = [
    "rgba(153, 235, 255, 1)",
    "rgba(76, 194, 255, 1)",
    "rgba(0, 145, 248, 1)",
    "rgba(0, 120, 212, 1)",
    "rgba(0, 103, 192, 1)",
    "rgba(0, 62, 146, 1)",
    "rgba(0, 26, 104, 1)",
];
// Will assign an alpha value to an RGB color string, or replace the alpha value of an RGBA color string.
function assignAlphaToColorString(oldRGBA, newA) {
    return oldRGBA.replace(/rgba?(\(\s*\d+\s*,\s*\d+\s*,\s*\d+)(?:\s*,.+?)?\)/, "rgba$1, " + newA + ")");
}
// Used to help implement application of the gradient stroke used for elevation. borderImageSource with a gradient would have been ideal
// and is used in Figma, but that does not work with borderRadius in actual CSS.
const gradientStroke = {
    "-webkit-mask": "linear-gradient(#FFFFFF 0 0) content-box, linear-gradient(#FFFFFF 0 0)",
    "-webkit-mask-composite": "destination-out",
    borderRadius: "inherit",
    bottom: 0,
    content: "''",
    left: 0,
    margin: "-1px",
    "mask-composite": "exclude",
    padding: "1px",
    position: "absolute",
    right: 0,
    top: 0,
};
function lightCustomSemanticElevations(accentColorRamp) {
    return {
        // customSemanticColors defined specifically for our app
        clockTextShadow: {
            textShadow: "none",
        },
        // customSemanticElevations defined in the Windows Visual Toolkit
        // Disable sort-keys lint rule so that we can match the order these are defined in the Windows Visual Toolkit Figma.
        /* eslint-disable sort-keys */
        control: {
            border: "solid 1px transparent",
            backgroundClip: "padding-box",
            "::before": Object.assign(Object.assign({}, gradientStroke), { background: "linear-gradient(180deg, rgba(0, 0, 0, 0.0578) 90.58%, rgba(0, 0, 0, 0.1622) 100%)" }),
        },
        controlPressed: {
            border: "solid 1px transparent",
            backgroundClip: "padding-box",
            "::before": Object.assign(Object.assign({}, gradientStroke), { background: "linear-gradient(180deg, rgba(0, 0, 0, 0.0578) 100%, rgba(0, 0, 0, 0.0578) 100%)" }),
        },
        controlOnAccent: {
            border: "solid 1px transparent",
            backgroundClip: "border-box",
            "::before": Object.assign(Object.assign({}, gradientStroke), { background: "linear-gradient(180deg, rgba(255, 255, 255, 0.08) 90.67%, rgba(0, 0, 0, 0.4) 100%)" }),
        },
        controlHoverOnAccent: {
            border: "solid 1px transparent",
            backgroundClip: "border-box",
            "::before": Object.assign(Object.assign({}, gradientStroke), { background: "linear-gradient(180deg, rgba(255, 255, 255, 0.08) 90.67%, rgba(0, 0, 0, 0.4) 100%)" }),
        },
        controlPressedOnAccent: {
            border: "solid 1px transparent",
            backgroundClip: "border-box",
            "::before": Object.assign(Object.assign({}, gradientStroke), { background: "linear-gradient(180deg, rgba(255, 255, 255, 0.08) 100%, rgba(255, 255, 255, 0.08) 100%)," }),
        },
        card: {
            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.04)",
        },
        cardHover: {
            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
        },
        cardPressed: {
            border: "1px solid rgba(0, 0, 0, 0.0578)",
            boxShadow: "none",
        },
        layer: {
            border: "1px solid rgba(0, 0, 0, 0.0578)",
        },
        tooltip: {
            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.14)",
        },
        flyout: {
            boxShadow: "0px 8px 16px rgba(0, 0, 0, 0.14)",
        },
        window: {
            boxShadow: "0px 32px 64px rgba(0, 0, 0, 0.24), 0px 0px 8px rgba(0, 0, 0, 0.2)",
        },
        /* eslint-enable sort-keys */
    };
}
function lightCustomSemanticColors(accentColorRamp) {
    return {
        // customSemanticColors defined specifically for our app
        badgeIconSignedOut: "rgba(0, 95, 183, 1)",
        iconButtonTranslucent: "rgba(255, 255, 255, 0.25)",
        textboxTranslucent: "rgba(0, 0, 0, 0.3)",
        widgetContextMenuButtonTranslucent: "rgba(0, 0, 0, 0.04)",
        // customSemanticColors defined in the Windows Visual Toolkit
        // Disable sort-keys lint rule so that we can match the order these are defined in the Windows Visual Toolkit Figma.
        /* eslint-disable sort-keys */
        textPrimary: "rgba(0, 0, 0, 0.8956)",
        textSecondary: "rgba(0, 0, 0, 0.6063)",
        textTertiary: "rgba(0, 0, 0, 0.4458)",
        textDisabled: "rgba(0, 0, 0, 0.3614)",
        accentTextPrimary: assignAlphaToColorString(accentColorRamp[5], "1"),
        accentTextSecondary: assignAlphaToColorString(accentColorRamp[6], "1"),
        accentTextTertiary: assignAlphaToColorString(accentColorRamp[4], "1"),
        accentTextDisabled: "rgba(0, 0, 0, 0.3614)",
        textOnAccentPrimary: "rgba(255, 255, 255, 1)",
        textOnAccentSecondary: "rgba(255, 255, 255, 0.7)",
        textOnAccentDisabled: "rgba(255, 255, 255, 1)",
        textOnAccentSelected: "rgba(255, 255, 255, 1)",
        controlFillDefault: "rgba(255, 255, 255, 0.7)",
        controlFillSecondary: "rgba(249, 249, 249, 0.5)",
        controlFillTertiary: "rgba(249, 249, 249, 0.3)",
        controlFillDisabled: "rgba(249, 249, 249, 0.3)",
        controlFillTransparent: "rgba(255, 255, 255, 0)",
        controlFillInputActive: "rgba(255, 255, 255, 1)",
        controlAltFillTransparent: "rgba(255, 255, 255, 0)",
        controlAltFillSecondary: "rgba(0, 0, 0, 0.0241)",
        controlAltFillTertiary: "rgba(0, 0, 0, 0.0578)",
        controlAltFillQuarternary: "rgba(0, 0, 0, 0.0924)",
        controlAltFillDisabled: "rgba(255, 255, 255, 0)",
        controlStrongFillDefault: "rgba(0, 0, 0, 0.4458)",
        controlStrongFillDisabled: "rgba(0, 0, 0, 0.3173)",
        controlSolidFillDefault: "rgba(255, 255, 255, 1)",
        subtleFillTransparent: "rgba(0, 0, 0, 0)",
        subtleFillSecondary: "rgba(0, 0, 0, 0.0373)",
        subtleFillTertiary: "rgba(0, 0, 0, 0.0241)",
        subtleFillDisabled: "rgba(0, 0, 0, 0)",
        controlOnImageFillDefault: "rgba(255, 255, 255, 0.79)",
        controlOnImageFillSecondary: "rgba(243, 243, 243, 1)",
        controlOnImageFillTertiary: "rgba(235, 235, 235, 1)",
        controlonImageFillDisabled: "rgba(0, 0, 0, 0)",
        accentFillDefault: assignAlphaToColorString(accentColorRamp[4], "1"),
        accentFillSecondary: assignAlphaToColorString(accentColorRamp[4], "0.9"),
        accentFillTertiary: assignAlphaToColorString(accentColorRamp[4], "0.8"),
        accentDisabled: "rgba(0, 0, 0, 0.2169)",
        accentFillSelectedTextBackground: assignAlphaToColorString(accentColorRamp[3], "1"),
        controlStrokeDefault: "rgba(0, 0, 0, 0.0578)",
        controlStrokeSecondary: "rgba(0, 0, 0, 0.1622)",
        controlStrokeOnAccentDefault: "rgba(255, 255, 255, 0.08)",
        controlStrokeOnAccentSecondary: "rgba(0, 0, 0, 0.4)",
        controlStrokeOnAccentTertiary: "rgba(0, 0, 0, 0.2169)",
        controlStrokeOnAccentDisabled: "rgba(0, 0, 0, 0.0578)",
        controlStrokeForStrongFillWhenOnImage: "rgba(255, 255, 255, 0.35)",
        controlGradientStrokeDefault: "linear-gradient(180deg, rgba(0, 0, 0, 0.0578) 90.58%, rgba(0, 0, 0, 0.1622) 100%)",
        controlGradientStrokeCircle: "linear-gradient(180deg, rgba(0, 0, 0, 0.0578) 50.02%, rgba(0, 0, 0, 0.1622) 95.45%)",
        controlGradientStrokeTextboxDefault: "linear-gradient(180deg, rgba(0, 0, 0, 0.0578) 96.87%, #8A8A8A 96.89%)",
        controlGradientStrokeTextboxSelected: "linear-gradient(180deg, rgba(0, 0, 0, 0.0578) 96.87%, #0067C0 96.89%)",
        controlGradientStrokeOnAccentDefault: "linear-gradient(180deg, rgba(255, 255, 255, 0.08) 90.67%, rgba(0, 0, 0, 0.4) 100%)",
        cardStrokeDefault: "rgba(0, 0, 0, 0.0578)",
        cardStrokeDefaultSolid: "rgba(235, 235, 235, 1)",
        controlStrongStrokeDefault: "rgba(0, 0, 0, 0.4458)",
        controlStrongStrokeDisabled: "rgba(0, 0, 0, 0.2169)",
        surfaceStrokeDefault: "rgba(0, 0, 0, 0.0578)",
        surfaceStrokeFlyout: "rgba(0, 0, 0, 0.0578)",
        dividerDefault: "rgba(0, 0, 0, 0.0578)",
        focusOuter: "rgba(0, 0, 0, 0.8956)",
        focusInner: "rgba(255, 255, 255, 0.7)",
        cardBackgroundDefault: "rgba(255, 255, 255, 0.7)",
        cardBackgroundSecondary: "rgba(246, 246, 246, 0.5)",
        smokeDefault: "rgba(255, 255, 255, 0.3)",
        smokeAA: "rgba(0, 0, 0, 0.42)",
        smokeAAA: "rgba(0, 0, 0, 0.54)",
        layerDefault: "rgba(255, 255, 255, 0.5)",
        layerAlt: "rgba(255, 255, 255, 1)",
        layerOnAcrylicDefault: "rgba(255, 255, 255, 0.25)",
        layerOnAccentAcrylicDefault: "rgba(255, 255, 255, 0.25)",
        layerOnThinAcrylicDefault: "rgba(255, 255, 255, 0.25)",
        solidBackgroundBase: "rgba(243, 243, 243, 1)",
        solidBackgroundSecondary: "rgba(238, 238, 238, 1)",
        solidBackgroundTertiary: "rgba(249, 249, 249, 1)",
        solidBackgroundQuarternary: "rgba(255, 255, 255, 1)",
        systemAttention: assignAlphaToColorString(accentColorRamp[4], "1"),
        systemAttentionBackground: "rgba(246, 246, 246, 0.5)",
        systemSuccess: "rgba(15, 123, 15, 1)",
        systemSuccessBackground: "rgba(223, 246, 221, 1)",
        systemCaution: "rgba(157, 93, 0, 1)",
        systemCautionBackground: "rgba(255, 244, 206, 1)",
        systemCritical: "rgba(196, 43, 28, 1)",
        systemCriticalBackground: "rgba(253, 231, 233, 1)",
        systemNeutral: "rgba(0, 0, 0, 0.4458)",
        systemNeutralBackground: "rgba(0, 0, 0, 0.0241)",
        /* eslint-enable sort-keys */
    };
}
function darkCustomSemanticElevations(accentColorRamp) {
    return {
        // customSemanticColors defined specifically for our app
        clockTextShadow: {
            textShadow: "0px 1px 2px rgba(0, 0, 0, 0.25), 0px 0px 2px rgba(0, 0, 0, 0.35)",
        },
        // customSemanticElevations defined in the Windows Visual Toolkit
        // Disable sort-keys lint rule so that we can match the order these are defined in the Windows Visual Toolkit Figma.
        /* eslint-disable sort-keys */
        control: {
            border: "solid 1px transparent",
            backgroundClip: "padding-box",
            "::before": Object.assign(Object.assign({}, gradientStroke), { background: "linear-gradient(180deg, rgba(255, 255, 255, 0.093) 0%, rgba(255, 255, 255, 0.0698) 9.57%)" }),
        },
        controlPressed: {
            border: "solid 1px transparent",
            backgroundClip: "padding-box",
            "::before": Object.assign(Object.assign({}, gradientStroke), { background: "linear-gradient(180deg, rgba(255, 255, 255, 0.0698) 100%, rgba(255, 255, 255, 0.0698) 100%)" }),
        },
        controlOnAccent: {
            border: "solid 1px transparent",
            backgroundClip: "border-box",
            "::before": Object.assign(Object.assign({}, gradientStroke), { background: "linear-gradient(180deg, rgba(255, 255, 255, 0.08) 90.67%, rgba(0, 0, 0, 0.14) 100%)" }),
        },
        controlHoverOnAccent: {
            border: "solid 1px transparent",
            backgroundClip: "border-box",
            "::before": Object.assign(Object.assign({}, gradientStroke), { background: "linear-gradient(180deg, rgba(255, 255, 255, 0.08) 90.67%, rgba(0, 0, 0, 0.14) 100%)" }),
        },
        controlPressedOnAccent: {
            border: "solid 1px transparent",
            backgroundClip: "border-box",
            "::before": Object.assign(Object.assign({}, gradientStroke), { background: "linear-gradient(180deg, rgba(0, 0, 0, 0.14) 100%, rgba(0, 0, 0, 0.14) 100%)" }),
        },
        card: {
            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.13)",
        },
        cardHover: {
            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.26)",
        },
        cardPressed: {
            border: "1px solid rgba(0, 0, 0, 0.1)",
            boxShadow: "none",
        },
        layer: {
            border: "1px solid rgba(0, 0, 0, 0.1)",
        },
        tooltip: {
            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.26)",
        },
        flyout: {
            boxShadow: "0px 8px 16px rgba(0, 0, 0, 0.26)",
        },
        window: {
            boxShadow: "0px 32px 64px rgba(0, 0, 0, 0.48), 0px 0px 8px rgba(0, 0, 0, 0.32)",
        },
        /* eslint-enable sort-keys */
    };
}
function darkCustomSemanticColors(accentColorRamp) {
    return {
        // customSemanticColors defined specifically for our app
        badgeIconSignedOut: "rgba(96, 205, 255, 1)",
        iconButtonTranslucent: "rgba(0, 0, 0, 0.2)",
        textboxTranslucent: "rgba(0, 0, 0, 0.3)",
        widgetContextMenuButtonTranslucent: "rgba(255, 255, 255, 0.09)",
        // customSemanticColors defined in the Windows Visual Toolkit
        // Disable sort-keys lint rule so that we can match the order these are defined in the Windows Visual Toolkit Figma.
        /* eslint-disable sort-keys */
        textPrimary: "rgba(255, 255, 255, 1)",
        textSecondary: "rgba(255, 255, 255, 0.786)",
        textTertiary: "rgba(255, 255, 255, 0.5442)",
        textDisabled: "rgba(255, 255, 255, 0.3628)",
        accentTextPrimary: assignAlphaToColorString(accentColorRamp[0], "1"),
        accentTextSecondary: assignAlphaToColorString(accentColorRamp[0], "1"),
        accentTextTertiary: assignAlphaToColorString(accentColorRamp[1], "1"),
        accentTextDisabled: "rgba(255, 255, 255, 0.3628)",
        textOnAccentPrimary: "rgba(0, 0, 0, 1)",
        textOnAccentSecondary: "rgba(0, 0, 0, 0.5)",
        textOnAccentDisabled: "rgba(255, 255, 255, 0.5302)",
        textOnAccentSelected: "rgba(255, 255, 255, 1)",
        controlFillDefault: "rgba(255, 255, 255, 0.0605)",
        controlFillSecondary: "rgba(255, 255, 255, 0.0837)",
        controlFillTertiary: "rgba(255, 255, 255, 0.0326)",
        controlFillDisabled: "rgba(255, 255, 255, 0.0419)",
        controlFillTransparent: "rgba(255, 255, 255, 0)",
        controlFillInputActive: "rgba(30, 30, 30, 0.7)",
        controlAltFillTransparent: "rgba(255, 255, 255, 0)",
        controlAltFillSecondary: "rgba(0, 0, 0, 0.1)",
        controlAltFillTertiary: "rgba(255, 255, 255, 0.0419)",
        controlAltFillQuarternary: "rgba(255, 255, 255, 0.0698)",
        controlAltFillDisabled: "rgba(255, 255, 255, 0)",
        controlStrongFillDefault: "rgba(255, 255, 255, 0.5442)",
        controlStrongFillDisabled: "rgba(255, 255, 255, 0.2465)",
        controlSolidFillDefault: "rgba(69, 69, 69, 1)",
        subtleFillTransparent: "rgba(255, 255, 255, 0)",
        subtleFillSecondary: "rgba(255, 255, 255, 0.0605)",
        subtleFillTertiary: "rgba(255, 255, 255, 0.0419)",
        subtleFillDisabled: "rgba(255, 255, 255, 0)",
        controlOnImageFillDefault: "rgba(28, 28, 28, 0.7)",
        controlOnImageFillSecondary: "rgba(26, 26, 26, 1)",
        controlOnImageFillTertiary: "rgba(19, 19, 19, 1)",
        controlonImageFillDisabled: "rgba(30, 30, 30, 1)",
        accentFillDefault: assignAlphaToColorString(accentColorRamp[1], "1"),
        accentFillSecondary: assignAlphaToColorString(accentColorRamp[1], "0.9"),
        accentFillTertiary: assignAlphaToColorString(accentColorRamp[1], "0.8"),
        accentDisabled: "rgba(255, 255, 255, 0.1581)",
        accentFillSelectedTextBackground: "rgba(0, 120, 212, 1)",
        controlStrokeDefault: "rgba(255, 255, 255, 0.0698)",
        controlStrokeSecondary: "rgba(255, 255, 255, 0.093)",
        controlStrokeOnAccentDefault: "rgba(255, 255, 255, 0.08)",
        controlStrokeOnAccentSecondary: "rgba(0, 0, 0, 0.14)",
        controlStrokeOnAccentTertiary: "rgba(0, 0, 0, 0.2169)",
        controlStrokeOnAccentDisabled: "rgba(0, 0, 0, 0.2)",
        controlStrokeForStrongFillWhenOnImage: "rgba(0, 0, 0, 0.42)",
        controlGradientStrokeDefault: "linear-gradient(180deg, rgba(255, 255, 255, 0.093) 0%, rgba(255, 255, 255, 0.0698) 9.57%)",
        controlGradientStrokeCircle: "linear-gradient(180deg, rgba(255, 255, 255, 0.093) 0%, rgba(255, 255, 255, 0.0698) 50%)",
        controlGradientStrokeTextboxDefault: "linear-gradient(180deg, rgba(255, 255, 255, 0.08) 99.98%, rgba(255, 255, 255, 0.5442) 96.89%, #989898 96.9%)",
        controlGradientStrokeTextboxSelected: "linear-gradient(180deg, rgba(255, 255, 255, 0.08) 96.89%, #4CC2FF 96.9%)",
        controlGradientStrokeOnAccentDefault: "linear-gradient(180deg, rgba(255, 255, 255, 0.08) 90.67%, rgba(0, 0, 0, 0.14) 100%)",
        cardStrokeDefault: "rgba(0, 0, 0, 0.1)",
        cardStrokeDefaultSolid: "rgba(28, 28, 28, 1)",
        controlStrongStrokeDefault: "rgba(255, 255, 255, 0.5442)",
        controlStrongStrokeDisabled: "rgba(255, 255, 255, 0.1581)",
        surfaceStrokeDefault: "rgba(255, 255, 255, 0.0837)",
        surfaceStrokeFlyout: "rgba(0, 0, 0, 0.2)",
        dividerDefault: "rgba(255, 255, 255, 0.0837)",
        focusOuter: "rgba(255, 255, 255, 1)",
        focusInner: "rgba(0, 0, 0, 0.7)",
        cardBackgroundDefault: "rgba(255, 255, 255, 0.0512)",
        cardBackgroundSecondary: "rgba(255, 255, 255, 0.0326)",
        smokeDefault: "rgba(44, 44, 44, 0.2)",
        smokeAA: "rgba(0, 0, 0, 0.42)",
        smokeAAA: "rgba(0, 0, 0, 0.54)",
        layerDefault: "rgba(58, 58, 58, 0.3)",
        layerAlt: "rgba(255, 255, 255, 0.0538)",
        layerOnAcrylicDefault: "rgba(255, 255, 255, 0.0359)",
        layerOnAccentAcrylicDefault: "rgba(255, 255, 255, 0.0359)",
        layerOnThinAcrylicDefault: "rgba(0, 0, 0, 0.2)",
        solidBackgroundBase: "rgba(32, 32, 32, 1)",
        solidBackgroundSecondary: "rgba(28, 28, 28, 1)",
        solidBackgroundTertiary: "rgba(40, 40, 40, 1)",
        solidBackgroundQuarternary: "rgba(44, 44, 44, 1)",
        systemAttention: assignAlphaToColorString(accentColorRamp[1], "1"),
        systemAttentionBackground: "rgba(255, 255, 255, 0.0326)",
        systemSuccess: "rgba(108, 203, 95, 1)",
        systemSuccessBackground: "rgba(57, 61, 27, 1)",
        systemCaution: "rgba(252, 225, 0, 1)",
        systemCautionBackground: "rgba(67, 53, 25, 1)",
        systemCritical: "rgba(255, 153, 164, 1)",
        systemCriticalBackground: "rgba(68, 39, 38, 1)",
        systemNeutral: "rgba(255, 255, 255, 0.5442)",
        systemNeutralBackground: "rgba(255, 255, 255, 0.0326)",
        /* eslint-enable sort-keys */
    };
}
// Define aspects of theme that are consistent across light and dark here.
export const appTheme = {
    // These font styles are used by the @microsoft/fluentui Text component based on these keys being passed in via its variant property.
    // We have defined these font styles based on the Windows Visual Toolkit Type Ramp, if any custom font styles are needed beyond these,
    // that can be provided via the usual component styling.
    /* eslint-disable sort-keys */
    fonts: {
        // "Display" in the Windows Visual Toolkit Type Ramp
        mega: {
            fontSize: "68px",
            lineHeight: "92px",
            fontWeight: 600,
        },
        // "Title Large" in the Windows Visual Toolkit Type Ramp
        superLarge: {
            fontSize: "40px",
            lineHeight: "52px",
            fontWeight: 600,
        },
        // "Title" in the Windows Visual Toolkit Type Ramp
        xxLarge: {
            fontSize: "28px",
            lineHeight: "36px",
            fontWeight: 600,
        },
        // "Subtitle" in the Windows Visual Toolkit Type Ramp
        xLarge: {
            fontSize: "20px",
            lineHeight: "28px",
            fontWeight: 600,
        },
        // "Body Large" in the Windows Visual Toolkit Type Ramp
        large: {
            fontSize: "18px",
            lineHight: "24px",
        },
        // "Body Strong" in the Windows Visual Toolkit Type Ramp
        mediumPlus: {
            fontSize: "14px",
            lineHeight: "20px",
            fontWeight: 600,
        },
        // "Body" in the Windows Visual Toolkit Type Ramp
        medium: {
            fontSize: "14px",
            lineHeight: "20px",
        },
        // "Caption" in the Windows Visual Toolkit Type Ramp
        small: {
            fontSize: "12px",
            lineHeight: "16px",
        },
    },
    /* eslint-enable sort-keys */
};
export function lightTheme(accentColorRampArg) {
    const accentColorRamp = accentColorRampArg || defaultAccentColorRamp;
    const lightCustomSemanticColorsWithAccentColorRamp = lightCustomSemanticColors(accentColorRamp);
    const lightCustomSemanticElevationsWithAccentColorRamp = lightCustomSemanticElevations(accentColorRamp);
    return Object.assign(Object.assign({}, appTheme), { customImages: {
            globalOfflineImage: "./images/offline-light.svg",
        }, customSemanticColors: Object.assign({}, lightCustomSemanticColorsWithAccentColorRamp), customSemanticElevations: Object.assign({}, lightCustomSemanticElevationsWithAccentColorRamp), 
        // These are the semanticColors referenced by @microsoft/fluentui components by default. We override these based on
        // Windows Visual Toolkit definitions and should generally just stick with the colors that the components pick up from
        // the semanticColors we've overriden.
        // When applying color to our own components prefer to directly use colors from customSemanticColors (which include
        // Windows Visual Toolkit colors and other semantic colors defined specifically for our app).
        semanticColors: {
            /* eslint-disable sort-keys */
            bodyText: lightCustomSemanticColorsWithAccentColorRamp.textPrimary,
            bodyTextChecked: lightCustomSemanticColorsWithAccentColorRamp.textPrimary,
            bodySubtext: lightCustomSemanticColorsWithAccentColorRamp.textSecondary,
            actionLink: lightCustomSemanticColorsWithAccentColorRamp.textPrimary,
            actionLinkHovered: lightCustomSemanticColorsWithAccentColorRamp.textPrimary,
            link: lightCustomSemanticColorsWithAccentColorRamp.accentTextPrimary,
            linkHovered: lightCustomSemanticColorsWithAccentColorRamp.accentTextSecondary,
            disabledText: lightCustomSemanticColorsWithAccentColorRamp.textDisabled,
            disabledBodyText: lightCustomSemanticColorsWithAccentColorRamp.textDisabled,
            disabledSubtext: lightCustomSemanticColorsWithAccentColorRamp.textDisabled,
            disabledBodySubtext: lightCustomSemanticColorsWithAccentColorRamp.textDisabled,
            errorText: lightCustomSemanticColorsWithAccentColorRamp.systemCritical,
            messageText: lightCustomSemanticColorsWithAccentColorRamp.textPrimary,
            inputText: lightCustomSemanticColorsWithAccentColorRamp.textPrimary,
            inputTextHovered: lightCustomSemanticColorsWithAccentColorRamp.textPrimary,
            inputPlaceholderText: lightCustomSemanticColorsWithAccentColorRamp.textSecondary,
            buttonText: lightCustomSemanticColorsWithAccentColorRamp.textPrimary,
            buttonTextHovered: lightCustomSemanticColorsWithAccentColorRamp.textPrimary,
            buttonTextChecked: lightCustomSemanticColorsWithAccentColorRamp.textSecondary,
            buttonTextCheckedHovered: lightCustomSemanticColorsWithAccentColorRamp.textOnAccentPrimary,
            buttonTextPressed: lightCustomSemanticColorsWithAccentColorRamp.textSecondary,
            buttonTextDisabled: lightCustomSemanticColorsWithAccentColorRamp.textDisabled,
            primaryButtonText: lightCustomSemanticColorsWithAccentColorRamp.textOnAccentPrimary,
            primaryButtonTextHovered: lightCustomSemanticColorsWithAccentColorRamp.textOnAccentPrimary,
            primaryButtonTextPressed: lightCustomSemanticColorsWithAccentColorRamp.textOnAccentSecondary,
            primaryButtonTextDisabled: lightCustomSemanticColorsWithAccentColorRamp.textOnAccentDisabled,
            accentButtonText: lightCustomSemanticColorsWithAccentColorRamp.textOnAccentPrimary,
            listText: lightCustomSemanticColorsWithAccentColorRamp.textPrimary,
            menuItemText: lightCustomSemanticColorsWithAccentColorRamp.textPrimary,
            menuItemTextHovered: lightCustomSemanticColorsWithAccentColorRamp.textPrimary,
            bodyBackground: lightCustomSemanticColorsWithAccentColorRamp.solidBackgroundBase,
            bodyBackgroundHovered: lightCustomSemanticColorsWithAccentColorRamp.subtleFillSecondary,
            bodyBackgroundChecked: lightCustomSemanticColorsWithAccentColorRamp.controlAltFillSecondary,
            bodyStandoutBackground: lightCustomSemanticColorsWithAccentColorRamp.solidBackgroundSecondary,
            bodyFrameBackground: lightCustomSemanticColorsWithAccentColorRamp.solidBackgroundBase,
            bodyFrameDivider: lightCustomSemanticColorsWithAccentColorRamp.dividerDefault,
            bodyDivider: lightCustomSemanticColorsWithAccentColorRamp.dividerDefault,
            disabledBackground: lightCustomSemanticColorsWithAccentColorRamp.controlFillDisabled,
            disabledBorder: lightCustomSemanticColorsWithAccentColorRamp.controlStrongStrokeDisabled,
            focusBorder: lightCustomSemanticColorsWithAccentColorRamp.focusOuter,
            cardStandoutBackground: lightCustomSemanticColorsWithAccentColorRamp.cardBackgroundDefault,
            cardShadow: lightCustomSemanticElevationsWithAccentColorRamp.card.boxShadow,
            cardShadowHovered: lightCustomSemanticElevationsWithAccentColorRamp.cardHover.boxShadow,
            variantBorder: lightCustomSemanticColorsWithAccentColorRamp.cardStrokeDefault,
            variantBorderHovered: lightCustomSemanticColorsWithAccentColorRamp.cardStrokeDefault,
            defaultStateBackground: lightCustomSemanticColorsWithAccentColorRamp.solidBackgroundSecondary,
            infoBackground: lightCustomSemanticColorsWithAccentColorRamp.systemAttentionBackground,
            errorBackground: lightCustomSemanticColorsWithAccentColorRamp.systemCriticalBackground,
            blockingBackground: lightCustomSemanticColorsWithAccentColorRamp.systemCriticalBackground,
            warningBackground: lightCustomSemanticColorsWithAccentColorRamp.systemCautionBackground,
            severeWarningBackground: lightCustomSemanticColorsWithAccentColorRamp.systemCautionBackground,
            successBackground: lightCustomSemanticColorsWithAccentColorRamp.systemSuccessBackground,
            infoIcon: lightCustomSemanticColorsWithAccentColorRamp.systemAttention,
            errorIcon: lightCustomSemanticColorsWithAccentColorRamp.systemCritical,
            blockingIcon: lightCustomSemanticColorsWithAccentColorRamp.systemCritical,
            warningIcon: lightCustomSemanticColorsWithAccentColorRamp.systemCaution,
            severeWarningIcon: lightCustomSemanticColorsWithAccentColorRamp.systemCaution,
            successIcon: lightCustomSemanticColorsWithAccentColorRamp.systemSuccess,
            messageLink: lightCustomSemanticColorsWithAccentColorRamp.accentTextPrimary,
            messageLinkHovered: lightCustomSemanticColorsWithAccentColorRamp.accentTextSecondary,
            inputBorder: "transparent",
            smallInputBorder: lightCustomSemanticColorsWithAccentColorRamp.controlStrongStrokeDefault,
            inputBorderHovered: "transparent",
            inputBackground: lightCustomSemanticColorsWithAccentColorRamp.controlFillDefault,
            inputBackgroundChecked: lightCustomSemanticColorsWithAccentColorRamp.accentFillDefault,
            inputBackgroundCheckedHovered: lightCustomSemanticColorsWithAccentColorRamp.accentFillSecondary,
            inputPlaceholderBackgroundChecked: lightCustomSemanticColorsWithAccentColorRamp.controlStrongFillDefault,
            inputForegroundChecked: lightCustomSemanticColorsWithAccentColorRamp.textOnAccentPrimary,
            inputFocusBorderAlt: lightCustomSemanticColorsWithAccentColorRamp.accentFillDefault,
            inputIconDisabled: lightCustomSemanticColorsWithAccentColorRamp.textDisabled,
            inputIcon: lightCustomSemanticColorsWithAccentColorRamp.textSecondary,
            inputIconHovered: lightCustomSemanticColorsWithAccentColorRamp.textSecondary,
            buttonBackground: lightCustomSemanticColorsWithAccentColorRamp.controlFillDefault,
            buttonBackgroundChecked: lightCustomSemanticColorsWithAccentColorRamp.accentFillDefault,
            buttonBackgroundHovered: lightCustomSemanticColorsWithAccentColorRamp.controlFillSecondary,
            buttonBackgroundCheckedHovered: lightCustomSemanticColorsWithAccentColorRamp.accentFillSecondary,
            buttonBackgroundDisabled: lightCustomSemanticColorsWithAccentColorRamp.controlFillDisabled,
            buttonBackgroundPressed: lightCustomSemanticColorsWithAccentColorRamp.controlFillTertiary,
            buttonBorder: "transparent",
            buttonBorderDisabled: "transparent",
            primaryButtonBackground: lightCustomSemanticColorsWithAccentColorRamp.accentFillDefault,
            primaryButtonBackgroundHovered: lightCustomSemanticColorsWithAccentColorRamp.accentFillSecondary,
            primaryButtonBackgroundPressed: lightCustomSemanticColorsWithAccentColorRamp.accentFillTertiary,
            primaryButtonBackgroundDisabled: lightCustomSemanticColorsWithAccentColorRamp.accentDisabled,
            primaryButtonBorder: "transparent",
            accentButtonBackground: lightCustomSemanticColorsWithAccentColorRamp.accentFillDefault,
            menuBackground: lightCustomSemanticColorsWithAccentColorRamp.solidBackgroundTertiary,
            menuDivider: lightCustomSemanticColorsWithAccentColorRamp.dividerDefault,
            menuIcon: lightCustomSemanticColorsWithAccentColorRamp.textPrimary,
            menuHeader: lightCustomSemanticColorsWithAccentColorRamp.textSecondary,
            menuItemBackgroundHovered: lightCustomSemanticColorsWithAccentColorRamp.subtleFillSecondary,
            menuItemBackgroundPressed: lightCustomSemanticColorsWithAccentColorRamp.subtleFillTertiary,
            listBackground: lightCustomSemanticColorsWithAccentColorRamp.solidBackgroundBase,
            listItemBackgroundHovered: lightCustomSemanticColorsWithAccentColorRamp.subtleFillSecondary,
            listItemBackgroundChecked: lightCustomSemanticColorsWithAccentColorRamp.subtleFillSecondary,
            listItemBackgroundCheckedHovered: lightCustomSemanticColorsWithAccentColorRamp.subtleFillTertiary,
            listHeaderBackgroundHovered: lightCustomSemanticColorsWithAccentColorRamp.subtleFillSecondary,
            listHeaderBackgroundPressed: lightCustomSemanticColorsWithAccentColorRamp.subtleFillTertiary,
            /* eslint-enable sort-keys */
        } });
}
export function darkTheme(accentColorRampArg) {
    const accentColorRamp = accentColorRampArg || defaultAccentColorRamp;
    const darkCustomSemanticColorsWithAccentColorRamp = darkCustomSemanticColors(accentColorRamp);
    const darkCustomSemanticElevationsWithAccentColorRamp = darkCustomSemanticElevations(accentColorRamp);
    return Object.assign(Object.assign({}, appTheme), { customImages: {
            globalOfflineImage: "./images/offline-dark.svg",
        }, customSemanticColors: Object.assign({}, darkCustomSemanticColorsWithAccentColorRamp), customSemanticElevations: Object.assign({}, darkCustomSemanticElevationsWithAccentColorRamp), 
        // These are the semanticColors referenced by @microsoft/fluentui components by default. We override these based on
        // Windows Visual Toolkit definitions and should generally just stick with the colors that the components pick up from
        // the semanticColors we've overriden.
        // When applying color to our own components prefer to directly use colors from customSemanticColors (which include
        // Windows Visual Toolkit colors and other semantic colors defined specifically for our app).
        semanticColors: {
            /* eslint-disable sort-keys */
            bodyText: darkCustomSemanticColorsWithAccentColorRamp.textPrimary,
            bodyTextChecked: darkCustomSemanticColorsWithAccentColorRamp.textPrimary,
            bodySubtext: darkCustomSemanticColorsWithAccentColorRamp.textSecondary,
            actionLink: darkCustomSemanticColorsWithAccentColorRamp.textPrimary,
            actionLinkHovered: darkCustomSemanticColorsWithAccentColorRamp.textPrimary,
            link: darkCustomSemanticColorsWithAccentColorRamp.accentTextPrimary,
            linkHovered: darkCustomSemanticColorsWithAccentColorRamp.accentTextSecondary,
            disabledText: darkCustomSemanticColorsWithAccentColorRamp.textDisabled,
            disabledBodyText: darkCustomSemanticColorsWithAccentColorRamp.textDisabled,
            disabledSubtext: darkCustomSemanticColorsWithAccentColorRamp.textDisabled,
            disabledBodySubtext: darkCustomSemanticColorsWithAccentColorRamp.textDisabled,
            errorText: darkCustomSemanticColorsWithAccentColorRamp.systemCritical,
            messageText: darkCustomSemanticColorsWithAccentColorRamp.textPrimary,
            inputText: darkCustomSemanticColorsWithAccentColorRamp.textPrimary,
            inputTextHovered: darkCustomSemanticColorsWithAccentColorRamp.textPrimary,
            inputPlaceholderText: darkCustomSemanticColorsWithAccentColorRamp.textSecondary,
            buttonText: darkCustomSemanticColorsWithAccentColorRamp.textPrimary,
            buttonTextHovered: darkCustomSemanticColorsWithAccentColorRamp.textPrimary,
            buttonTextChecked: darkCustomSemanticColorsWithAccentColorRamp.textSecondary,
            buttonTextCheckedHovered: darkCustomSemanticColorsWithAccentColorRamp.textOnAccentPrimary,
            buttonTextPressed: darkCustomSemanticColorsWithAccentColorRamp.textSecondary,
            buttonTextDisabled: darkCustomSemanticColorsWithAccentColorRamp.textDisabled,
            primaryButtonText: darkCustomSemanticColorsWithAccentColorRamp.textOnAccentPrimary,
            primaryButtonTextHovered: darkCustomSemanticColorsWithAccentColorRamp.textOnAccentPrimary,
            primaryButtonTextPressed: darkCustomSemanticColorsWithAccentColorRamp.textOnAccentSecondary,
            primaryButtonTextDisabled: darkCustomSemanticColorsWithAccentColorRamp.textOnAccentDisabled,
            accentButtonText: darkCustomSemanticColorsWithAccentColorRamp.textOnAccentPrimary,
            listText: darkCustomSemanticColorsWithAccentColorRamp.textPrimary,
            menuItemText: darkCustomSemanticColorsWithAccentColorRamp.textPrimary,
            menuItemTextHovered: darkCustomSemanticColorsWithAccentColorRamp.textPrimary,
            bodyBackground: darkCustomSemanticColorsWithAccentColorRamp.solidBackgroundBase,
            bodyBackgroundHovered: darkCustomSemanticColorsWithAccentColorRamp.subtleFillSecondary,
            bodyBackgroundChecked: darkCustomSemanticColorsWithAccentColorRamp.controlAltFillSecondary,
            bodyStandoutBackground: darkCustomSemanticColorsWithAccentColorRamp.solidBackgroundSecondary,
            bodyFrameBackground: darkCustomSemanticColorsWithAccentColorRamp.solidBackgroundBase,
            bodyFrameDivider: darkCustomSemanticColorsWithAccentColorRamp.dividerDefault,
            bodyDivider: darkCustomSemanticColorsWithAccentColorRamp.dividerDefault,
            disabledBackground: darkCustomSemanticColorsWithAccentColorRamp.controlFillDisabled,
            disabledBorder: darkCustomSemanticColorsWithAccentColorRamp.controlStrongStrokeDisabled,
            focusBorder: darkCustomSemanticColorsWithAccentColorRamp.focusOuter,
            cardStandoutBackground: darkCustomSemanticColorsWithAccentColorRamp.cardBackgroundDefault,
            cardShadow: darkCustomSemanticElevationsWithAccentColorRamp.card.boxShadow,
            cardShadowHovered: darkCustomSemanticElevationsWithAccentColorRamp.cardHover.boxShadow,
            variantBorder: darkCustomSemanticColorsWithAccentColorRamp.cardStrokeDefault,
            variantBorderHovered: darkCustomSemanticColorsWithAccentColorRamp.cardStrokeDefault,
            defaultStateBackground: darkCustomSemanticColorsWithAccentColorRamp.solidBackgroundSecondary,
            infoBackground: darkCustomSemanticColorsWithAccentColorRamp.systemAttentionBackground,
            errorBackground: darkCustomSemanticColorsWithAccentColorRamp.systemCriticalBackground,
            blockingBackground: darkCustomSemanticColorsWithAccentColorRamp.systemCriticalBackground,
            warningBackground: darkCustomSemanticColorsWithAccentColorRamp.systemCautionBackground,
            severeWarningBackground: darkCustomSemanticColorsWithAccentColorRamp.systemCautionBackground,
            successBackground: darkCustomSemanticColorsWithAccentColorRamp.systemSuccessBackground,
            infoIcon: darkCustomSemanticColorsWithAccentColorRamp.systemAttention,
            errorIcon: darkCustomSemanticColorsWithAccentColorRamp.systemCritical,
            blockingIcon: darkCustomSemanticColorsWithAccentColorRamp.systemCritical,
            warningIcon: darkCustomSemanticColorsWithAccentColorRamp.systemCaution,
            severeWarningIcon: darkCustomSemanticColorsWithAccentColorRamp.systemCaution,
            successIcon: darkCustomSemanticColorsWithAccentColorRamp.systemSuccess,
            messageLink: darkCustomSemanticColorsWithAccentColorRamp.accentTextPrimary,
            messageLinkHovered: darkCustomSemanticColorsWithAccentColorRamp.accentTextSecondary,
            inputBorder: "transparent",
            smallInputBorder: darkCustomSemanticColorsWithAccentColorRamp.controlStrongStrokeDefault,
            inputBorderHovered: "transparent",
            inputBackground: darkCustomSemanticColorsWithAccentColorRamp.controlFillDefault,
            inputBackgroundChecked: darkCustomSemanticColorsWithAccentColorRamp.accentFillDefault,
            inputBackgroundCheckedHovered: darkCustomSemanticColorsWithAccentColorRamp.accentFillSecondary,
            inputPlaceholderBackgroundChecked: darkCustomSemanticColorsWithAccentColorRamp.controlStrongFillDefault,
            inputForegroundChecked: darkCustomSemanticColorsWithAccentColorRamp.textOnAccentPrimary,
            inputFocusBorderAlt: darkCustomSemanticColorsWithAccentColorRamp.accentFillDefault,
            inputIconDisabled: darkCustomSemanticColorsWithAccentColorRamp.textDisabled,
            inputIcon: darkCustomSemanticColorsWithAccentColorRamp.textSecondary,
            inputIconHovered: darkCustomSemanticColorsWithAccentColorRamp.textSecondary,
            buttonBackground: darkCustomSemanticColorsWithAccentColorRamp.controlFillDefault,
            buttonBackgroundChecked: darkCustomSemanticColorsWithAccentColorRamp.accentFillDefault,
            buttonBackgroundHovered: darkCustomSemanticColorsWithAccentColorRamp.controlFillSecondary,
            buttonBackgroundCheckedHovered: darkCustomSemanticColorsWithAccentColorRamp.accentFillSecondary,
            buttonBackgroundDisabled: darkCustomSemanticColorsWithAccentColorRamp.controlFillDisabled,
            buttonBackgroundPressed: darkCustomSemanticColorsWithAccentColorRamp.controlFillTertiary,
            buttonBorder: "transparent",
            buttonBorderDisabled: "transparent",
            primaryButtonBackground: darkCustomSemanticColorsWithAccentColorRamp.accentFillDefault,
            primaryButtonBackgroundHovered: darkCustomSemanticColorsWithAccentColorRamp.accentFillSecondary,
            primaryButtonBackgroundPressed: darkCustomSemanticColorsWithAccentColorRamp.accentFillTertiary,
            primaryButtonBackgroundDisabled: darkCustomSemanticColorsWithAccentColorRamp.accentDisabled,
            primaryButtonBorder: "transparent",
            accentButtonBackground: darkCustomSemanticColorsWithAccentColorRamp.accentFillDefault,
            menuBackground: darkCustomSemanticColorsWithAccentColorRamp.solidBackgroundTertiary,
            menuDivider: darkCustomSemanticColorsWithAccentColorRamp.dividerDefault,
            menuIcon: darkCustomSemanticColorsWithAccentColorRamp.textPrimary,
            menuHeader: darkCustomSemanticColorsWithAccentColorRamp.textSecondary,
            menuItemBackgroundHovered: darkCustomSemanticColorsWithAccentColorRamp.subtleFillSecondary,
            menuItemBackgroundPressed: darkCustomSemanticColorsWithAccentColorRamp.subtleFillTertiary,
            listBackground: darkCustomSemanticColorsWithAccentColorRamp.solidBackgroundBase,
            listItemBackgroundHovered: darkCustomSemanticColorsWithAccentColorRamp.subtleFillSecondary,
            listItemBackgroundChecked: darkCustomSemanticColorsWithAccentColorRamp.subtleFillSecondary,
            listItemBackgroundCheckedHovered: darkCustomSemanticColorsWithAccentColorRamp.subtleFillTertiary,
            listHeaderBackgroundHovered: darkCustomSemanticColorsWithAccentColorRamp.subtleFillSecondary,
            listHeaderBackgroundPressed: darkCustomSemanticColorsWithAccentColorRamp.subtleFillTertiary,
            /* eslint-enable sort-keys */
        } });
}
